import { useEffect, useState } from "react";
import { applyReading, fetchLink, isStored, pickedFromSlide, readFlyer, searchUrl, storeFlyer, type Picked } from "../../lib/flyer";
import { loneReel, reelFrom, where } from "../../lib/flyerViewer";
import { fLong } from "../../lib/format";
import { logValue } from "../../lib/sheets";
import { addBand, blankDraft, deleteShow, draftFromShow, draftFromUpcoming, draftProblem, fetchCatalog, fetchLoggable, fetchPeople, fetchShow, knownBand, knownVenue, logShow, moveItem, newBands, newBandsNote, removeAt, saveShow, toggle } from "../../lib/showSheet";
import type { Catalog, Draft, Person, ShowDetail, WentPrompt } from "../../lib/types";
import { billOf } from "../../lib/upcomingPage";
import { useApp } from "../appState";
import { Flyer } from "../Flyer";
import { FlyerField, FlyerSource } from "../FlyerTools";
import { Sheet } from "../Sheet";
import { VenueFinder } from "../VenueFinder";

/** Log a show's third start: an Upcoming listing RSVP'd Going, from today or earlier, which reopens the sheet filled from it. */
function UpcomingPicker({ onPick }: { onPick: (id: number) => void }) {
  const { zoom } = useApp();
  const [listings, setListings] = useState<WentPrompt[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    fetchLoggable().then((l) => live && setListings(l), (e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, []);
  if (error) return <p className="error">{error}</p>;
  if (!listings) return <p className="loading">Loading…</p>;
  if (!listings.length) return <p className="muted" style={{ margin: 0 }}>Nothing from Upcoming to log. A show you RSVP'd Going to shows up here on the day of the show.</p>;
  return (
    <div className="pickl">
      {listings.map((u) => (
        <div className="pickr" key={u.id}>
          <Flyer url={u.image_url} alt={`Flyer for ${u.event_name}`}
            text={{ date: u.date, bands: billOf(u.event_name), venue: u.venue }}
            onZoom={() => zoom(reelFrom(listings, u, (l) =>
              l.image_url ? { url: l.image_url, title: l.event_name, sub: where(l.venue, l.date) } : null))} />
          <button type="button" onClick={() => onPick(u.id)}>
            <b>{u.event_name}</b>
            <span className="muted">{u.venue} · {fLong(u.date)}</span>
          </button>
        </div>
      ))}
    </div>
  );
}

/** Log a show (blank or from an Upcoming listing) and Edit a show share this form. */
export function DraftSheet({ mode, showId, upcomingId }: { mode: "log" | "edit"; showId?: number; upcomingId?: number | null }) {
  const { open, close, changed, today, zoom } = useApp();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [show, setShow] = useState<ShowDetail | null>(null);
  const [d, setD] = useState<Draft | null>(null);
  const [band, setBand] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  // The chosen image, stored only when the sheet saves.
  const [picked, setPicked] = useState<Picked | null>(null);
  // Log a show starts from a flyer photo (the redesign); a listing or an edit starts filled in.
  const [start, setStart] = useState<"flyer" | "upcoming" | "blank">(mode === "log" && !upcomingId ? "flyer" : "blank");
  const [reading, setReading] = useState<{ notes: string | null } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // Save waits for the listing-flyer copy, or a quick tap would log the show without it (gate, 2026-09-11).
  const [copying, setCopying] = useState(false);
  // Who was there is optional and folded away; names only (Matt, 2026-09-11).
  const [whoOpen, setWhoOpen] = useState(false);

  useEffect(() => {
    let live = true;
    const start: Promise<Draft> =
      mode === "edit" && showId
        ? fetchShow(showId).then((s) => {
            if (live) setShow(s);
            return draftFromShow(s);
          })
        : upcomingId
          ? draftFromUpcoming(upcomingId)
          : Promise.resolve(blankDraft(today));
    Promise.all([fetchCatalog(), fetchPeople(), start])
      .then(([c, p, draft]) => {
        if (!live) return;
        setCatalog(c);
        setPeople(p);
        // A listing's flyer outside our bucket is copied into attended/ on save;
        // fetching it now also shows it in the sheet.
        const copy = mode === "log" && draft.image_url && !isStored(draft.image_url) ? draft.image_url : null;
        setD(copy ? { ...draft, image_url: null } : draft);
        if (copy) {
          setCopying(true);
          fetchLink(copy)
            .then(
              (got) => live && setPicked((cur) => cur ?? pickedFromSlide(got.slides[0], got.caption)),
              () => live && setNote("The listing's flyer could not be copied. Add one below."),
            )
            .finally(() => live && setCopying(false));
        }
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [mode, showId, upcomingId, today]);

  const title = mode === "edit" ? `Edit #${show?.number ?? ""}` : "Log a show";
  if (!d || !catalog) {
    return (
      <Sheet title={title} label={title} onClose={close}>
        {error ? <p className="error">{error}</p> : <p className="loading">Loading…</p>}
      </Sheet>
    );
  }

  const problem = draftProblem(d, catalog);
  const newVenue = d.venue_name.trim() && !knownVenue(catalog, d.venue_name);
  // A bill that arrived on its own (read from a flyer, or split out of an
  // Upcoming listing) was matched to the catalog by the server; whatever it
  // could not match is a band this show would add, so say so before saving.
  const fresh = newBands(catalog, d.bands);
  const imported = Boolean(reading) || d.import_upcoming_id !== null;
  const set = (patch: Partial<Draft>) => setD({ ...d, ...patch });
  const add = () => {
    set({ bands: addBand(d.bands, band) });
    setBand("");
  };
  const takeReading = async (p: Picked) => {
    const r = await readFlyer(p);
    setD((cur) => (cur ? applyReading(cur, r.draft) : cur));
    setPicked(p);
    setReading({ notes: r.notes });
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Named after the date and headliner being saved, so it is stored only now.
      const image_url = picked ? await storeFlyer(picked, d.bands[0] ?? "", d.date) : d.image_url;
      const draft = { ...d, image_url };
      const saved = mode === "edit" && showId ? await saveShow(showId, draft, catalog) : await logShow(draft, catalog);
      changed(mode === "edit" ? "Saved" : `Logged show #${saved.number}`);
      open("show", saved.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet title={title} label={title} onClose={mode === "edit" && showId ? () => open("show", showId) : close}>
      {mode === "log" && !upcomingId && (
        <div className="seg" role="group" aria-label="Start from">
          <button type="button" aria-pressed={start === "flyer"} onClick={() => { setStart("flyer"); setError(null); }}>From a flyer</button>
          <button type="button" aria-pressed={start === "upcoming"} onClick={() => { setStart("upcoming"); setError(null); }}>From Upcoming</button>
          <button type="button" aria-pressed={start === "blank"} onClick={() => { setStart("blank"); setError(null); }}>Blank</button>
        </div>
      )}
      {start === "upcoming" && <UpcomingPicker onPick={(id) => open("log", logValue(id))} />}
      {start === "flyer" && !reading && (
        <div className="drop">
          <b>Start from a flyer photo</b>
          <span>The bill, date and venue are read from it, and it becomes the show's flyer.</span>
          <FlyerSource primary onPick={takeReading} busyText="Reading the flyer…" />
        </div>
      )}
      {reading && picked && (
        <div className="parsed">
          <Flyer url={picked.preview} alt="The flyer" onZoom={() => zoom(loneReel(picked.preview, "The flyer you picked"))} />
          <div>
            <b>Read from the flyer.</b>
            <br />
            <span className="muted">Check the bill, date and venue below; the flyer is saved with the show.</span>
            {reading.notes && <span className="faint" style={{ display: "block", marginTop: 4 }}>It also says: {reading.notes}</span>}
          </div>
        </div>
      )}
      {(start === "blank" || reading) && (
        <>
        <div className="form">
          <div className="two">
            <label className="f">
              <span>Date{reading && <span className="from"> · read from flyer</span>}</span>
              <input type="date" value={d.date} onChange={(e) => set({ date: e.target.value })} />
            </label>
            <label className="f">
              <span>Event</span>
              <input list="dl-events" value={d.event_name} placeholder="Festival or tour (optional)" onChange={(e) => set({ event_name: e.target.value })} />
            </label>
          </div>
          <div className="f">
            <span>Bill, headliner first{reading && <span className="from"> · read from flyer</span>}</span>
            {imported && d.bands.length > 0 && (
              <p className="billnew">{newBandsNote(d.bands.length, fresh.length)}</p>
            )}
            <div className="billed">
              {d.bands.map((b, i) => (
                <div className="bi" key={`${b}${i}`}>
                  <span className="n">{i + 1}</span>
                  <span>
                    {b}
                    {!knownBand(catalog, b) && <span className="tagnew">new</span>}
                  </span>
                  <span className="ops">
                    <button type="button" aria-label={`Move ${b} up`} onClick={() => set({ bands: moveItem(d.bands, i, -1) })}>↑</button>
                    <button type="button" aria-label={`Move ${b} down`} onClick={() => set({ bands: moveItem(d.bands, i, 1) })}>↓</button>
                    <button type="button" aria-label={`Remove ${b}`} onClick={() => set({ bands: removeAt(d.bands, i) })}>✕</button>
                  </span>
                </div>
              ))}
              <div className="add">
                <input list="dl-bands" value={band} placeholder="Add a band (autocompletes)" aria-label="Add a band"
                  onChange={(e) => setBand(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
                <button type="button" className="btn sm" disabled={!band.trim()} onClick={add}>Add</button>
              </div>
            </div>
          </div>
          <label className="f">
            <span>Venue{reading && <span className="from"> · read from flyer</span>}</span>
            <input list="dl-venues" value={d.venue_name} placeholder="Search venues" onChange={(e) => set({ venue_name: e.target.value, venue_place: null })} />
          </label>
          {newVenue && (
            <div className="f">
              <label className="f">
                <span>New venue: its address{d.venue_place && <span className="from"> · linked to Google Maps</span>}</span>
                <input value={d.venue_location} placeholder="Street, city, state" onChange={(e) => set({ venue_location: e.target.value, venue_place: null })} />
              </label>
              <VenueFinder query={`${d.venue_name} ${d.venue_location}`}
                onPick={(p) => set({ venue_name: p.name, venue_location: p.address, venue_place: { place_id: p.place_id, latitude: p.latitude, longitude: p.longitude } })} />
            </div>
          )}
          <div className="f">
            <button type="button" className="fold" aria-expanded={whoOpen} onClick={() => setWhoOpen(!whoOpen)}>
              <span>Who was there? <span className="faint">optional</span></span>
              <span>{d.solo ? "Solo" : d.person_ids.length || ""} {whoOpen ? "▾" : "▸"}</span>
            </button>
            {whoOpen && (
              <div className="ptag plain">
                {people.map((p) => (
                  <button type="button" key={p.id} aria-pressed={!d.solo && d.person_ids.includes(p.id)} onClick={() => set({ solo: false, person_ids: toggle(d.person_ids, p.id) })}>
                    {p.name}
                  </button>
                ))}
                <button type="button" aria-pressed={d.solo} onClick={() => set({ solo: !d.solo })}>Solo</button>
              </div>
            )}
          </div>
          <FlyerField url={d.image_url} picked={picked} title={d.event_name || d.bands[0]}
            search={searchUrl({ event: d.event_name, bands: d.bands, venue: d.venue_name, date: d.date })}
            onPick={setPicked} onRemove={() => { setPicked(null); set({ image_url: null }); }} />
          {copying && <p className="loading" style={{ margin: 0 }}>Copying the listing's flyer…</p>}
        {note && <p className="faint" style={{ margin: 0, fontSize: 12 }}>{note}</p>}
        </div>
        <datalist id="dl-bands">{catalog.bands.map((b) => <option key={b} value={b} />)}</datalist>
        <datalist id="dl-venues">{catalog.venues.map((v) => <option key={v.name} value={v.name} />)}</datalist>
        <datalist id="dl-events">{catalog.events.map((e) => <option key={e} value={e} />)}</datalist>
        {error && <p className="error">{error}</p>}
        {problem && <p className="faint" style={{ margin: 0, fontSize: 12 }}>{problem}</p>}
        <div className="sheet-acts">
          <button type="button" className={`btn ${mode === "log" ? "primary" : ""}`} disabled={Boolean(problem) || saving || copying} onClick={save}>
            {mode === "edit" ? "Save" : "Log show"}
          </button>
          <button type="button" className="btn ghost" onClick={mode === "edit" && showId ? () => open("show", showId) : close}>Cancel</button>
        </div>
        </>
      )}
      {start === "flyer" && !reading && error && <p className="error">{error}</p>}
      {mode === "edit" && showId && (confirm ? (
        <div className="confirm">
          <span>Delete show #{show?.number} and its {show?.media.length ? `${show.media.length} media links` : "bill"}? This can't be undone.</span>
          <div className="sheet-acts">
            <button type="button" className="btn danger" onClick={() => deleteShow(showId).then(() => { changed("Deleted"); close(); }, (e: Error) => setError(e.message))}>
              Delete forever
            </button>
            <button type="button" className="btn ghost sm" onClick={() => setConfirm(false)}>Keep it</button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn danger" style={{ justifySelf: "start" }} onClick={() => setConfirm(true)}>Delete show</button>
      ))}
    </Sheet>
  );
}
